import cloneDeep from 'lodash-es/cloneDeep';

import {
  BroadcastActionComponent,
  BroadcastActionComponentType,
  BroadcastComponentParameter,
  Form,
  WhatsappTemplate,
  WhatsappTemplateComponent,
} from '@/shared/types/broadcast';

/**
 * Extracts variable names from a component's text.
 *
 * @param component - The component containing text with variables
 * @returns An array of variable names (without the curly braces)
 */
export const getVariablesFromComponent = (
  component: WhatsappTemplateComponent | BroadcastActionComponent
): string[] => {
  if (!component || !component.text) {
    return [];
  }

  // Match both positional parameters {{digit}} and named parameters {{name}}
  const pattern = /\{\{([a-zA-Z0-9_]+)\}\}/g;
  const matches = component.text.match(pattern);

  if (!matches) {
    return [];
  }

  // Extract the variable names without the curly braces
  return matches.map((match) => match.replace(/[{}]/g, ''));
};

export const getVariablesLength = (
  component: WhatsappTemplateComponent | BroadcastActionComponent
) => {
  if (!component) {
    return 0;
  }

  if (component?.text) {
    return getVariablesFromComponent(component).length;
  }

  return 0;
};

/**
 * Determines if a given component is the HEADER and of type IMAGE | VIDEO | DOCUMENT.
 *
 * @param {BroadcastActionComponent | WhatsappTemplateComponent} c - The component to check.
 * @returns {boolean} - Returns true if the component is a media header (i.e., its type is 'header' and its format is not 'text'), otherwise false.
 *
 */

export const isMediaHeader = (c: BroadcastActionComponent | WhatsappTemplateComponent) =>
  c?.type.toLowerCase() === 'header' && c.format.toLowerCase() !== 'text';

/**
 * Extracts variable names from named parameter examples in a component.
 *
 * @param component - The component containing named parameter examples
 * @param componentType - The type of component (header, body, etc.)
 * @returns An array of variable names
 */
export const getVariableNamesFromNamedParams = (
  component: WhatsappTemplateComponent | BroadcastActionComponent,
  componentType: 'header' | 'body' | 'footer'
): string[] => {
  if (!component || !component.example) {
    return [];
  }

  const namedParamsKey = `${componentType}_text_named_params`;
  const namedParams = component.example[namedParamsKey];

  if (namedParams && Array.isArray(namedParams)) {
    // Check if the structure contains param_name or parameter_name
    if (namedParams.length > 0) {
      if ('param_name' in namedParams[0]) {
        return namedParams.map((param) => param.param_name);
      } else if ('parameter_name' in namedParams[0]) {
        return namedParams.map((param) => param.parameter_name);
      }
    }
  }

  // Fallback: extract from text if named params not found
  return getVariablesFromComponent(component).filter((name) => isNaN(Number(name)));
};

/**
 * Formats the components of a WhatsApp template based on provided form data.
 *
 * @param {WhatsappTemplate} template - The WhatsApp template object containing components to format.
 * @param {Form} formData - The form data containing parameter values for the template components.
 * @returns {BroadcastActionComponent[]} - An array of formatted broadcast action components.
 */
export const formatComponentsFromForm = (
  template: WhatsappTemplate,
  formData: Form
): BroadcastActionComponent[] => {
  const components = template?.components.map((c) => {
    const type = `${c.type?.toLowerCase()}Parameters`;
    const componentType = c.type.toLowerCase() as 'header' | 'body' | 'footer';

    const items = formData[type]?.filter((item) => item.name);
    let parameters = [];
    if (!isMediaHeader(c)) {
      // Check if we're using named parameters
      const isNamedParams = template.parameter_format === 'NAMED';

      // Get variable names for named parameters
      const variableNames = isNamedParams ? getVariableNamesFromNamedParams(c, componentType) : [];

      parameters = items?.map((param, index) => {
        // Create the base parameter object
        const paramObject: BroadcastComponentParameter & { parameter_name?: string } = {
          type: 'text',
          text: param?.name?.value || param?.name || '',
        };

        // Add parameter_name for named parameters
        if (isNamedParams && index < variableNames.length) {
          paramObject.parameter_name = variableNames[index];
        }

        return paramObject;
      });
    } else {
      const mediaType = c.format.toLowerCase() as 'image' | 'document' | 'video';

      const value =
        typeof formData.headerMediaParam.value === 'string'
          ? formData.headerMediaParam.value
          : formData.headerMediaParam.value?.value;

      const parameter = {
        type: mediaType,
        [mediaType]: {
          id: formData.headerMediaParam.type === 'id' ? value : undefined,
          link: formData.headerMediaParam.type === 'link' ? value : undefined,
        },
      };

      parameters.push(parameter);
    }
    const newComponent: BroadcastActionComponent = {
      ...c,
      type: c.type.toLowerCase() as BroadcastActionComponentType,
      parameters,
    };
    return newComponent;
  });
  return components;
};

export const getComponentsFromTemplate = (template: WhatsappTemplate) => {
  const header = template?.components?.find((component) => component.type === 'HEADER');
  const body = template?.components?.find((component) => component.type === 'BODY');
  const footer = template?.components?.find((component) => component.type === 'FOOTER');

  const buttons = template?.components?.find((component) => component.type === 'BUTTONS')?.buttons;

  return { header, body, footer, buttons };
};

/**
 *
 * This function replaces all occurrences of placeholders in the format `{{number}}`
 * with incremented numbers starting from `startIndex`.
 *
 * @param startIndex - The initial index to start numbering from.
 * @param  text - The text containing placeholders to update.
 */

export const startVariablesFromIndex = (startIndex: number, text: string) => {
  let currentIndex = startIndex;

  const updatedText = text.replace(/\{\{\d+\}\}/g, () => `{{${++currentIndex}}}`);

  return updatedText;
};

/**
 * Transforms named parameters from param_name to parameter_name in template components.
 * This is specifically for API requests to ensure compatibility.
 *
 * @param template - The WhatsApp template to transform
 * @returns A new template with param_name transformed to parameter_name
 */
export const transformParamNameToParameterName = (template: WhatsappTemplate): WhatsappTemplate => {
  if (!template) {
    return template;
  }

  // Deep clone the template to avoid mutating the original
  const transformedTemplate = cloneDeep(template);

  // Transform param_name to parameter_name in all components
  transformedTemplate.components.forEach((component) => {
    if (component.example) {
      // Handle header_text_named_params
      if (Array.isArray(component.example.header_text_named_params)) {
        component.example.header_text_named_params = component.example.header_text_named_params.map(
          (param) => {
            // Create a new object for each parameter to avoid reference issues
            const newParam = { ...param };
            if ('param_name' in newParam) {
              newParam['parameter_name'] = newParam.param_name;
              delete newParam.param_name;
            }
            return newParam;
          }
        );
      }

      // Handle body_text_named_params
      if (Array.isArray(component.example.body_text_named_params)) {
        component.example.body_text_named_params = component.example.body_text_named_params.map(
          (param) => {
            // Create a new object for each parameter to avoid reference issues
            const newParam = { ...param };
            if ('param_name' in newParam) {
              newParam['parameter_name'] = newParam.param_name;
              delete newParam.param_name;
            }
            return newParam;
          }
        );
      }
    }
  });

  return transformedTemplate;
};

export const filterMessageWhatsappTemplate = (templates: WhatsappTemplate[]) => {
  return templates?.reduce((acc, template) => {
    const { header, buttons } = getComponentsFromTemplate(template);

    if (template.status !== 'APPROVED' || (header && header?.format !== 'TEXT')) {
      return acc;
    }

    const buttonWithParams = buttons?.find((button) => button.example);

    if (buttons && buttonWithParams) {
      return acc;
    }
    return [...acc, template];
  }, [] as WhatsappTemplate[]);
};

/**
 * Extracts variable names from a WhatsApp template component
 */
export const extractVariableNames = (
  template: WhatsappTemplate,
  name: 'body' | 'header' | 'footer'
): string[] => {
  if (template.parameter_format !== 'NAMED' || !template.components[0]) {
    return [];
  }

  // Find the component for the current section
  const component = template.components.find((comp) => comp.type.toLowerCase() === name);

  if (!component) {
    return [];
  }

  // For named parameters, first check if we have named parameter examples
  const namedParamsKey = `${name}_text_named_params`;
  const namedParams = component.example?.[namedParamsKey];

  if (namedParams && Array.isArray(namedParams)) {
    // If we have named parameter examples, use their names
    const extractedNames = namedParams.map((param) => param.param_name);
    if (extractedNames.length > 0) {
      return extractedNames;
    }
  }

  // Otherwise, extract variable names from the component text
  // This is a fallback for templates that don't have named parameter examples
  const extractedNames = getVariablesFromComponent(component).filter((name) => isNaN(Number(name))); // Filter out positional parameters

  return extractedNames.length > 0 ? extractedNames : [];
};
